import { plural, type Locale, type Messages } from '@tms/i18n';
import type { AuditEntry } from '../api/endpoints';
import { formatTime } from './format';

/**
 * Запись журнала аудита — словами (FR-521).
 *
 * Журнал читает человек: «Назначен курьер · Рустам Ахмедов», а не `assigned` и JSON.
 * Служебные поля (`requirement`, `actor_kind`, `task_id`, `device_id`) не выводятся
 * никогда; сырые значения остаются под «технические данные» у администратора.
 * Неизвестный код показывается как есть — новое событие не должно пропадать из
 * журнала только потому, что для него ещё нет слов.
 */
export interface AuditText {
  readonly event: string;
  readonly details: string;
  /** `false` — события нет в словаре, показан его код. */
  readonly known: boolean;
}

type Value = Record<string, unknown>;

/**
 * `reasonNames` — справочник причин заказчика (код → название): коды недоставки и
 * отмены приходят оттуда, а не из словаря интерфейса.
 */
export function describeAudit(
  entry: AuditEntry,
  messages: Messages,
  locale: Locale,
  reasonNames: Readonly<Record<string, string>> = {},
): AuditText {
  const labels = messages.cabinet.journal;
  const text = labels.text;
  const next = asValue(entry.new_value);
  const previous = asValue(entry.old_value);
  const refs = entry.refs ?? {};
  const name = (id: unknown): string | null => (typeof id === 'string' ? (refs[id] ?? null) : null);
  const known = (event: string, ...details: (string | null | undefined)[]): AuditText => ({
    event,
    details: details
      .filter((part): part is string => part !== null && part !== undefined && part !== '')
      .join(' · '),
    known: true,
  });
  const reason = (value: unknown): string | null =>
    typeof value === 'string' && value !== ''
      ? text.reason.replace(
          '{reason}',
          reasonNames[value] ?? (text.reasons as Record<string, string>)[value] ?? value,
        )
      : null;

  switch (entry.action) {
    case 'web_login':
      return known(
        labels.events.web_login,
        browserOf(next['user_agent'], text.serviceClient),
        ipOf(next['ip'], text.ip),
      );

    case 'created':
      if (entry.entity === 'order') {
        return known(
          entry.actor.kind === 'ct'
            ? labels.events.order_created
            : labels.events.order_created_manual,
          modeLabel(messages, next['mode']),
        );
      }

      if (entry.entity === 'shift') {
        return known(
          labels.events.shift_created,
          modeLabel(messages, next['mode']),
          permissionsOf(next, text),
        );
      }

      if (entry.entity === 'driver') {
        return known(labels.events.driver_created, str(next['full_name']));
      }

      if (entry.entity === 'executor_message') {
        return known(labels.events.message_created);
      }

      break;

    case 'updated':
      if (entry.entity === 'order') {
        return known(labels.events.order_updated, changesOf(previous, next, messages, name));
      }

      break;

    case 'coordinates_set': {
      const point = asValue(next['point']);
      const zone = name(next['zone_id']);

      return known(
        labels.events.coordinates_set,
        typeof point['lat'] === 'number' && typeof point['lng'] === 'number'
          ? `${point['lat'].toFixed(2)}, ${point['lng'].toFixed(2)}`
          : null,
        zone === null ? null : text.zone.replace('{zone}', zone),
      );
    }

    case 'status_changed': {
      const from = statusOf(messages, previous['status']);
      const to = statusOf(messages, next['status']);

      return known(
        labels.events.status_changed,
        to === null ? null : `${from ?? ''} → ${to}`.trim(),
        reason(next['reason']),
      );
    }

    case 'assigned':
      return known(
        labels.events.assigned,
        name(next['driver_id']),
        next['forced'] === true ? text.forced : null,
        reason(next['force_reason']),
      );

    case 'distribution_planned': {
      const stops = typeof next['planned_stops'] === 'number' ? next['planned_stops'] : null;
      const late = typeof next['late_stops'] === 'number' ? next['late_stops'] : 0;

      return known(
        labels.events.distribution_planned,
        stops === null
          ? null
          : `${plural(locale, stops, text.stops)}, ${late === 0 ? text.noLate : text.late.replace('{count}', String(late))}`,
        next['trigger'] === 'driver'
          ? text.triggerDriver
          : next['trigger'] === 'urgent_order'
            ? text.triggerUrgent
            : null,
      );
    }

    case 'route_edited_by_driver':
      return known(labels.events.route_edited_by_driver, movedOf(previous, next, text));

    case 'stops_reordered':
      return known(labels.events.stops_reordered, reason(next['reason']));

    case 'reassigned':
      return known(labels.events.reassigned, name(next['driver_id']), reason(next['reason']));

    case 'session_revoked':
      return known(
        labels.events.session_revoked,
        next['reason'] === 'another_device' ? text.anotherDevice : str(next['reason']),
      );

    case 'modes_changed': {
      const modes = Array.isArray(next['modes']) ? next['modes'] : [];

      return known(
        labels.events.modes_changed,
        modes.length === 0
          ? text.noDepartments
          : modes.map((mode) => modeLabel(messages, mode)).join(', '),
      );
    }

    case 'courier_action': {
      const kind = str(next['kind']);
      const kinds = text.courierKinds as Record<string, string>;

      return known(labels.events.courier_action, kind === null ? null : (kinds[kind] ?? kind));
    }

    case 'permission_denied':
      return known(labels.events.permission_denied);

    case 'access_denied':
      return known(
        labels.events.access_denied,
        next['reason'] === 'signature_mismatch' ? text.signatureMismatch : str(next['reason']),
        ipOf(next['ip'], text.ip),
      );

    case 'parameter_changed':
      return known(
        labels.events.parameter_changed,
        `${scalar(previous['value'])} → ${scalar(next['value'])}`,
      );

    case 'replied':
      return known(labels.events.message_replied, str(next['reply']));

    case 'code_issued':
    case 'session_opened':
    case 'data_accessed':
    case 'object_mismatch':
    case 'installation_switch':
    case 'admin_bootstrapped':
    case 'lookup_missed':
      return known(labels.events[entry.action]);

    default:
      break;
  }

  return { event: entry.action, details: '', known: false };
}

/** Объект строки: «Заказ A-000885», «Смена: Экспедитор». Идентификатор — никогда. */
export function objectLabel(entry: AuditEntry, messages: Messages): string {
  const objects = messages.cabinet.journal.objects as Record<string, string>;
  const kind = entry.object?.kind ?? entry.entity ?? '';
  const title = objects[kind];
  const label = entry.object?.label ?? null;

  if (title === undefined) {
    return label ?? '';
  }

  if (label === null) {
    return title;
  }

  return kind === 'order' ? `${title} ${label}` : `${title}: ${label}`;
}

/** Автор: имя и роль словами; CT и система — по названию. */
export function actorLabel(
  entry: AuditEntry,
  messages: Messages,
): { name: string; role: string | null } {
  const kind = entry.actor.kind ?? '';
  const roles = messages.cabinet.users.roles as Record<string, string>;
  const actors = messages.cabinet.journal.actors as Record<string, string>;

  if (actors[kind] !== undefined) {
    return { name: actors[kind], role: null };
  }

  return {
    name: entry.actor.name ?? roles[kind] ?? kind,
    role:
      entry.actor.name === null || entry.actor.name === undefined ? null : (roles[kind] ?? null),
  };
}

function asValue(value: unknown): Value {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Value)
    : {};
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function scalar(value: unknown): string {
  return value === null || value === undefined
    ? '—'
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);
}

function ipOf(value: unknown, template: string): string | null {
  return typeof value === 'string' ? template.replace('{ip}', value) : null;
}

/** Браузер по строке клиента; автоматический клиент — «служебный клиент». */
function browserOf(value: unknown, service: string): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  if (/headless|node|curl|axios|undici|postman/i.test(value)) {
    return service;
  }

  for (const [pattern, title] of [
    [/edg\//i, 'Edge'],
    [/opr\//i, 'Opera'],
    [/yabrowser/i, 'Yandex'],
    [/firefox/i, 'Firefox'],
    [/chrome/i, 'Chrome'],
    [/safari/i, 'Safari'],
  ] as const) {
    if (pattern.test(value)) {
      return title;
    }
  }

  return service;
}

function modeLabel(messages: Messages, mode: unknown): string | null {
  return mode === 'express' || mode === 'distribution' ? messages.cabinet.modes[mode] : null;
}

function statusOf(messages: Messages, status: unknown): string | null {
  if (typeof status !== 'string') {
    return null;
  }

  const labels = messages.status as Record<string, string>;

  return labels[status] ?? status;
}

function permissionsOf(value: Value, text: Messages['cabinet']['journal']['text']): string | null {
  const permissions = asValue(value['permissions']);

  if (Object.keys(permissions).length === 0) {
    return null;
  }

  const granted = (key: string): string => (permissions[key] === 'granted' ? text.yes : text.no);

  return [
    `${text.geolocation}: ${granted('background_location')}`,
    `${text.notifications}: ${granted('notifications')}`,
    `${text.batterySaver}: ${permissions['battery_saver_active'] === true ? text.yes : text.no}`,
  ].join(', ');
}

/** Куда курьер перенёс точку: в начало, в конец или на N-е место. */
function movedOf(
  previous: Value,
  next: Value,
  text: Messages['cabinet']['journal']['text'],
): string | null {
  const stop = str(next['stop_id']);
  const sequence = Array.isArray(next['sequence']) ? next['sequence'] : [];
  const target = str(next['target_at']);

  if (target !== null) {
    return text.targetAt.replace('{time}', formatTime(target));
  }

  const index = stop === null ? -1 : sequence.indexOf(stop);
  const before = Array.isArray(previous['sequence']) ? previous['sequence'].indexOf(stop) : -1;

  if (index < 0 || index === before) {
    return null;
  }

  if (index === sequence.length - 1) {
    return text.toEnd;
  }

  return index === 0 ? text.toStart : text.toPosition.replace('{position}', String(index + 1));
}

/** Изменённые поля заказа: «зона: — → Показ». Сравниваются только известные поля. */
function changesOf(
  previous: Value,
  next: Value,
  messages: Messages,
  name: (id: unknown) => string | null,
): string | null {
  const text = messages.cabinet.journal.text;
  const fields: readonly [string, string, (value: unknown) => string | null][] = [
    ['zone_id', text.fieldZone, name],
    ['external_number', text.fieldNumber, str],
    ['pickup_location_id', text.fieldPickup, name],
    ['mode', text.fieldMode, (value) => modeLabel(messages, value)],
  ];

  const changes = fields
    .filter(([key]) => key in next && key in previous && previous[key] !== next[key])
    .map(
      ([key, title, show]) => `${title}: ${show(previous[key]) ?? '—'} → ${show(next[key]) ?? '—'}`,
    );

  return changes.length === 0 ? null : changes.join(', ');
}
